import React, { useCallback, useEffect, useState } from "react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import Spinner from "../../Layouts/Spinner";
import "./ShopifyMembershipMonitor.css";

const ShopifyMembershipMonitor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("shopify_monitor/list");
      setData(response?.data?.response || null);
    } catch (error) {
      console.error(error);
      swal({
        text: "Unable to load Shopify membership monitoring",
        icon: "error",
        button: "Close",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? String(value)
      : parsed.toLocaleString("en-SA");
  };

  const summary = data?.summary || {};
  const heartbeat = data?.heartbeat;
  const items = data?.items || [];

  return (
    <div className="shopify-monitor-page">
      <div className="category-wrapper">
        <div className="shopify-monitor-heading">
          <div>
            <h3>Shopify Membership Monitor</h3>
            <p>
              Provider status, local membership alignment, and reconciliation
              worker health.
            </p>
          </div>
          <button type="button" onClick={loadData} disabled={loading}>
            {loading ? <Spinner /> : "Refresh"}
          </button>
        </div>

        <div className="shopify-monitor-summary">
          <div>
            <span>Open issues</span>
            <strong>{summary.open_memberships || 0}</strong>
          </div>
          <div>
            <span>Period mismatches</span>
            <strong>{summary.period_mismatches || 0}</strong>
          </div>
          <div>
            <span>Provider blocked</span>
            <strong>{summary.blocked_memberships || 0}</strong>
          </div>
          <div>
            <span>Payment confirmation</span>
            <strong>{summary.payment_pending || 0}</strong>
          </div>
          <div>
            <span>Worker</span>
            <strong>{summary.worker_status || "not reporting"}</strong>
          </div>
        </div>

        <div className="shopify-monitor-worker">
          <span>Last scan: {formatDate(heartbeat?.last_completed_on)}</span>
          <span>Due: {heartbeat?.due_count || 0}</span>
          <span>Queued: {heartbeat?.enqueued_count || 0}</span>
          <span>Failures: {heartbeat?.failure_count || 0}</span>
          {heartbeat?.error_code && (
            <span>Error: {heartbeat.error_code}</span>
          )}
        </div>
      </div>

      <div className="category-wrapper mt-3">
        <div className="shopify-monitor-table-wrap">
          <table className="shopify-monitor-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Customer</th>
                <th>State</th>
                <th>Shopify status</th>
                <th>Shopify period end</th>
                <th>Local period end</th>
                <th>Payment</th>
                <th>Payment period</th>
                <th>Access</th>
                <th>Reason</th>
                <th>Last checked</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center">
                    No Shopify membership checks have been recorded yet.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.user_id}>
                  <td>{item.user_id}</td>
                  <td>{item.customer_name || "-"}</td>
                  <td>
                    <span
                      className={`shopify-monitor-state ${
                        item.sync_state === "healthy" ? "healthy" : "attention"
                      }`}
                    >
                      {item.sync_state || "-"}
                    </span>
                  </td>
                  <td>{item.provider_status || "-"}</td>
                  <td>{item.provider_period_end || "-"}</td>
                  <td>{item.local_period_end || "-"}</td>
                  <td>{item.payment_confirmation_state || "-"}</td>
                  <td>{item.payment_period_end || "-"}</td>
                  <td>{item.access_allowed ? "Allowed" : "Blocked"}</td>
                  <td>
                    {item.payment_confirmation_reason || item.reason_code || "-"}
                  </td>
                  <td>{formatDate(item.last_checked_on)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ShopifyMembershipMonitor;
